import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// Shadow Grid Theme Colors for Admin
export const adminColors = {
  primary: '#00d4ff',
  primaryDark: '#00a4ff',
  secondary: '#ffffff',
  background: '#0d1a25',
  surface: 'rgba(20, 45, 70, 0.95)',
  surfaceHover: 'rgba(30, 60, 90, 0.95)',
  surfaceLight: 'rgba(40, 75, 110, 0.9)',
  text: '#ffffff',
  textSecondary: '#ffffff',
  textMuted: '#ffffff',
  border: 'rgba(0, 212, 255, 0.5)',
  borderLight: 'rgba(0, 212, 255, 0.35)',
  borderMuted: 'rgba(0, 212, 255, 0.22)',
  success: '#5ddb5d',
  error: '#ff6b6b',
  warning: '#ffcc00',
};

// Common styles
const glowEffect = css`
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.15);
`;

const cardStyle = css`
  background: ${adminColors.surface};
  border: 1px solid ${adminColors.border};
  border-radius: 12px;
  backdrop-filter: blur(10px);
  ${glowEffect}
`;

// Layout
export const AdminContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 20px 20px 20px;
  color: ${adminColors.text};

  @media (max-width: 768px) {
    padding: 80px 15px 20px 15px;
  }
`;

export const AdminPageTitle = styled.h1`
  font-family: 'Rajdhani', sans-serif;
  font-size: 3rem;
  color: ${adminColors.primary};
  text-align: center;
  margin-bottom: 10px;
  text-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 3px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

export const AdminStatusBar = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: ${adminColors.textMuted};
  text-align: center;
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

export const AdminMainContent = styled.div`
  display: flex;
  gap: 24px;
  height: calc(100vh - 220px);
  min-height: 500px;

  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
  }
`;

// Sidebar Container - фиксированная ширина, без скролла
export const AdminSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: visible;
  max-height: 100%;
  width: 280px;
  flex-shrink: 0;
`;

// Control Panel (Sidebar) - скролл внутри панели
export const AdminControlPanel = styled.div`
  ${cardStyle}
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  max-height: 100%;
`;

// Контейнер для навигации со скроллом
export const AdminControlPanelNav = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 212, 255, 0.5);
  }
`;

// Content Area - фиксированная ширина
export const AdminContentArea = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100%;
  padding-right: 8px;
  width: 1050px;
  flex-shrink: 0;

  @media (max-width: 1400px) {
    width: 100%;
    flex: 1;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 212, 255, 0.5);
  }
`;

export const AdminControlPanelHeader = styled.div`
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(0, 164, 255, 0.1) 100%);
  padding: 20px;
  text-align: center;
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${adminColors.primary};
  text-transform: uppercase;
  letter-spacing: 2px;
  border-bottom: 1px solid ${adminColors.borderLight};
`;

export const AdminNavItem = styled(Link)<{ $active?: boolean }>`
  display: block;
  margin: 8px 12px;
  padding: 14px 18px;
  text-decoration: none;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${props => props.$active ? adminColors.primary : adminColors.textMuted};
  background: ${props => props.$active ? 'rgba(0, 212, 255, 0.12)' : 'transparent'};
  border-radius: 8px;
  transition: all 0.3s ease;
  border-left: 3px solid ${props => props.$active ? adminColors.primary : 'transparent'};

  &:hover {
    background: rgba(0, 212, 255, 0.08);
    color: ${adminColors.primary};
    transform: translateX(4px);
  }
`;

export const AdminNavGroup = styled.div<{ $expanded?: boolean }>`
  margin: 8px 12px;
`;

export const AdminNavGroupHeader = styled.div<{ $active?: boolean; $expanded?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${props => props.$active ? adminColors.primary : adminColors.textMuted};
  background: ${props => props.$active ? 'rgba(0, 212, 255, 0.12)' : 'transparent'};
  border-radius: 8px;
  transition: all 0.3s ease;
  border-left: 3px solid ${props => props.$active ? adminColors.primary : 'transparent'};

  &:hover {
    background: rgba(0, 212, 255, 0.08);
    color: ${adminColors.primary};
  }

  &::after {
    content: '${props => props.$expanded ? '▼' : '▶'}';
    font-size: 10px;
    color: ${adminColors.textMuted};
    transition: transform 0.3s ease;
  }
`;

export const AdminNavSubMenu = styled.div<{ $expanded?: boolean }>`
  max-height: ${props => props.$expanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-left: 12px;
  border-left: 1px solid ${adminColors.borderLight};
`;

export const AdminNavSubItem = styled(Link)<{ $active?: boolean }>`
  display: block;
  padding: 10px 16px;
  margin: 4px 0;
  text-decoration: none;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.$active ? adminColors.primary : adminColors.textMuted};
  background: ${props => props.$active ? 'rgba(0, 212, 255, 0.08)' : 'transparent'};
  border-radius: 0 6px 6px 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.06);
    color: ${adminColors.primary};
    padding-left: 20px;
  }
`;

export const AdminInfoBox = styled.div`
  margin: 16px 12px;
  padding: 16px;
  background: rgba(0, 212, 255, 0.06);
  border: 1px solid ${adminColors.borderLight};
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: ${adminColors.secondary};
  line-height: 1.8;
`;

export const AdminSystemStatus = styled.div`
  margin: 16px 12px;
  padding: 16px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.06);
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: ${adminColors.success};

  h3 {
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 1px;
  }
`;

export const AdminStatusItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(76, 175, 80, 0.2);

  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

// Cards and Panels
export const AdminCard = styled.div`
  ${cardStyle}
  padding: 24px;
  margin-bottom: 20px;
`;

export const AdminCardHeader = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.5rem;
  color: ${adminColors.text};
  text-align: center;
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
`;

// Tables
export const AdminTable = styled.div`
  ${cardStyle}
  overflow: hidden;
  margin-bottom: 20px;
`;

export const AdminTableHeader = styled.div`
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 164, 255, 0.08) 100%);
  padding: 16px 20px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${adminColors.primary};
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid ${adminColors.borderLight};
`;

export const AdminTableRow = styled.div<{ $header?: boolean }>`
  display: grid;
  padding: 14px 20px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: ${adminColors.text};
  background: ${props => props.$header ? 'rgba(0, 212, 255, 0.05)' : 'transparent'};
  border-bottom: 1px solid ${adminColors.borderMuted};
  align-items: center;
  transition: background 0.2s ease;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.2);

  &:hover {
    background: ${props => props.$header ? 'rgba(0, 212, 255, 0.05)' : 'rgba(0, 212, 255, 0.03)'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

// Buttons
export const AdminPrimaryButton = styled.button`
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(0, 164, 255, 0.15) 100%);
  border: 1px solid ${adminColors.primary};
  color: ${adminColors.primary};
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 0 15px rgba(0, 212, 255, 0.2);

  &:hover {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(0, 164, 255, 0.25) 100%);
    box-shadow: 0 0 25px rgba(0, 212, 255, 0.4);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const AdminSecondaryButton = styled.button`
  background: transparent;
  border: 1px solid ${adminColors.borderLight};
  color: ${adminColors.textMuted};
  font-family: 'Courier New', monospace;
  font-size: 13px;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${adminColors.border};
    color: ${adminColors.text};
    background: rgba(0, 212, 255, 0.05);
  }
`;

export const AdminDangerButton = styled.button`
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.4);
  color: ${adminColors.error};
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 68, 68, 0.2);
    border-color: ${adminColors.error};
  }
`;

export const AdminInlineButton = styled.button`
  background: transparent;
  border: 1px solid ${adminColors.borderLight};
  color: ${adminColors.secondary};
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: ${adminColors.primary};
    color: ${adminColors.primary};
  }
`;

// Forms
export const AdminForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const AdminFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AdminFormLabel = styled.label`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: ${adminColors.text};
  text-transform: uppercase;
  letter-spacing: 1px;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
`;

export const AdminFormInput = styled.input`
  background: rgba(0, 20, 40, 0.6);
  border: 1px solid ${adminColors.border};
  border-radius: 8px;
  padding: 12px 16px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${adminColors.primary};
    box-shadow: 0 0 12px rgba(0, 212, 255, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

export const AdminFormTextarea = styled.textarea`
  background: rgba(0, 20, 40, 0.6);
  border: 1px solid ${adminColors.border};
  border-radius: 8px;
  padding: 12px 16px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${adminColors.primary};
    box-shadow: 0 0 12px rgba(0, 212, 255, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

export const AdminFormSelect = styled.select`
  background: rgba(0, 20, 40, 0.6);
  border: 1px solid ${adminColors.border};
  border-radius: 8px;
  padding: 12px 16px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${adminColors.primary};
    box-shadow: 0 0 12px rgba(0, 212, 255, 0.3);
  }

  option {
    background: #0a1520;
    color: #ffffff;
  }
`;

export const AdminCheckbox = styled.input`
  accent-color: ${adminColors.primary};
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

export const AdminCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  cursor: pointer;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.2);
`;

// Status badges
export const AdminStatusBadge = styled.span<{ $status: 'active' | 'inactive' | 'warning' | 'error' }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.$status) {
      case 'active':
        return css`
          background: rgba(76, 175, 80, 0.15);
          border: 1px solid rgba(76, 175, 80, 0.4);
          color: ${adminColors.success};
        `;
      case 'inactive':
        return css`
          background: rgba(176, 176, 176, 0.1);
          border: 1px solid rgba(176, 176, 176, 0.3);
          color: ${adminColors.textMuted};
        `;
      case 'warning':
        return css`
          background: rgba(255, 170, 0, 0.15);
          border: 1px solid rgba(255, 170, 0, 0.4);
          color: ${adminColors.warning};
        `;
      case 'error':
        return css`
          background: rgba(255, 68, 68, 0.15);
          border: 1px solid rgba(255, 68, 68, 0.4);
          color: ${adminColors.error};
        `;
    }
  }}
`;

// Chips/Tags
export const AdminChip = styled.span`
  display: inline-block;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid ${adminColors.borderLight};
  border-radius: 4px;
  padding: 4px 8px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: ${adminColors.secondary};
`;

export const AdminChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

// Messages
export const AdminErrorMessage = styled.div`
  color: ${adminColors.error};
  font-family: 'Courier New', monospace;
  font-size: 13px;
  padding: 12px 16px;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  border-radius: 8px;
`;

export const AdminSuccessMessage = styled.div`
  color: ${adminColors.success};
  font-family: 'Courier New', monospace;
  font-size: 13px;
  padding: 12px 16px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
`;

export const AdminLoadingMessage = styled.div`
  color: ${adminColors.primary};
  font-family: 'Courier New', monospace;
  font-size: 13px;
  text-align: center;
  padding: 20px;
`;

// Metrics
export const AdminMetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

export const AdminMetricCard = styled.div`
  ${cardStyle}
  padding: 24px;
  text-align: center;
`;

export const AdminMetricLabel = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: ${adminColors.secondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

export const AdminMetricValue = styled.div`
  font-family: 'Rajdhani', sans-serif;
  font-size: 3rem;
  font-weight: 700;
  color: ${adminColors.text};
  text-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
  margin-bottom: 8px;
`;

export const AdminMetricFooter = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: ${adminColors.primary};
  text-transform: uppercase;
`;

// Form Actions
export const AdminFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

// Footer
export const AdminFooter = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: ${adminColors.textMuted};
  text-align: center;
  margin-top: 60px;
  margin-bottom: 20px;
  letter-spacing: 1px;
`;

// Logout button
export const AdminLogoutButton = styled.button`
  background: linear-gradient(135deg, rgba(255, 68, 68, 0.2) 0%, rgba(204, 51, 51, 0.15) 100%);
  border: 1px solid rgba(255, 68, 68, 0.5);
  color: ${adminColors.error};
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(255, 68, 68, 0.3) 0%, rgba(204, 51, 51, 0.25) 100%);
    box-shadow: 0 0 20px rgba(255, 68, 68, 0.3);
    transform: translateY(-2px);
  }
`;

// Empty state
export const AdminEmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${adminColors.textMuted};
  font-family: 'Courier New', monospace;
  font-size: 14px;
`;
